import React, { useContext, useEffect, useState } from 'react';
import './Users.scss';
import { observer } from 'mobx-react';
import { rootContext } from 'stores';
import {
  Button,
  Card,
  DatePicker,
  Form,
  Image,
  Input,
  Modal,
  Popconfirm,
  Select,
  Switch,
  Table,
  Tag
} from 'antd';
import { PageHeader } from '@ant-design/pro-components';
import {
  EditFilled,
  MailOutlined,
  QuestionCircleOutlined,
  SearchOutlined
} from '@ant-design/icons';
import { IUser, pagination, sortByParams } from 'types';
import { SorterResult, TablePaginationConfig } from 'antd/lib/table/interface';
import { sortOrderType } from 'common/utils';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import Craeteuser from 'components/Craeteuser/Craeteuser';
import { getCreateUserTypes } from 'common/utils';
import { useDebounceState } from 'hooks/useDebounce.hook';
import { RouteConfigs } from 'config/routes';

const layout = {
  labelCol: { span: 6 },
  wrapperCol: { span: 16 }
};
const Users: React.FC = observer(() => {
  const {
    RegionStore: {
      getRegions,
      state: {
        data: { regions },
        ui: { getRegionsLoading }
      }
    },
    UsersRegions: {
      state: {
        data: { userRegions, getUserRegionsParams },
        ui: { isUserRegionsLoading }
      },
      getUserRegions
    },
    UsersStore: {
      state,
      state: { data, ui },
      setState,
      getUsers,
      disableUser,
      editUser
    },
    AuthStore: {
      isAdmin,
      isDistributor,
      isEmployee,
      sendEmailWithCreds,
      state: {
        data: { user },
        ui: { sendEmailWithCredsLoading }
      }
    }
  } = useContext(rootContext);

  const [editForm] = Form.useForm();

  const [editUserData, setEditUserData] = useState<null | IUser>(null);
  const [addUserModal, setAddUserModal] = useState<boolean>(false);
  const [sessionViewData, setSessionViewData] = useState<any>(null);
  const [search, deSearch, setSearch] = useDebounceState('', 500);
  const dateFormatList = ['DD/MM/YYYY', 'DD/MM/YY'];
  const [subscriptionEndDate, setSubscriptionDate] = useState<null | number>(null);
  const [subscriptionStartDate, setSubscriptionStartDate] = useState<null | number>(null);
  const [userType, setUserType] = useState('');

  const tableOnChange = (
    pagination: TablePaginationConfig,
    _: any,
    sort: any
  ) => {
    sort = sort as SorterResult<IUser>;
    const sortField = sort.columnKey as string;
    const sortBy = sort.order && sortOrderType(sort.order);
    const limit = pagination.pageSize || data.getUsersParams.limit;
    const offset = ((pagination?.current || 1) - 1) * limit;

    let getUsersParams: sortByParams & pagination = {
      limit,
      offset
    };

    if (sortField && sortBy) {
      getUsersParams = { ...getUsersParams, sortBy, sortField };
    }

    setState({
      data: {
        ...state.data,
        getUsersParams
      }
    });
  };

  useEffect(() => {
    getUsers({ ...data.getUsersParams, search: deSearch });
    getUserRegions({ ...getUserRegionsParams, limit: 9999999 });
    getRegions();
  }, [
    getUsers,
    editUserData,
    data.getUsersParams,
    deSearch,
    getRegions,
    getUserRegions,
    getUserRegionsParams
  ]);
  function onChange(date: any, dateString: any) {
    setSubscriptionDate(date?.unix(dateString));
  }

  function onChangeStartDate(date: any, dateString: any) {
    setSubscriptionStartDate(date?.unix(dateString));
  }

  function disabledDate(current: any) {
    return current && current < dayjs().startOf('day');
  }
  const navigate = useNavigate();
  return (
    <div className="UsersStyles PartsStyles">
      <Craeteuser
        addUserModal={addUserModal}
        setAddUserModal={setAddUserModal}
      />
      <div className="cardWrapeer">
        <Card>
          <PageHeader
            title="Users"
            onBack={() => navigate(-1)}
            extra={
              <div>
                <Input
                  className="search"
                  placeholder="Search"
                  addonBefore={<SearchOutlined />}
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
                {isAdmin || isDistributor || isEmployee ? (
                  <Button onClick={() => setAddUserModal(true)}>
                    Add User
                  </Button>
                ) : (
                  ''
                )}
              </div>
            }
          />
          <Table<IUser>
            rowKey="id"
            className="Table_block_main"
            loading={ui.isUsersLoading}
            dataSource={data.users}
            pagination={{
              defaultCurrent: 1,
              defaultPageSize: data.getUsersParams.limit,
              total: data.total || 0,
              showSizeChanger: true,
              pageSizeOptions: ['10', '15', '20']
            }}
            onChange={(pagination, _, sort) =>
              tableOnChange(pagination, _, sort)
            }
            onRow={(record, rowIndex) => {
              return {
                onClick: event => {
                  if (isNaN((event.target as HTMLTableCellElement).cellIndex))
                    return;
                  navigate(RouteConfigs.userDetail.getPath(record.id));
                }
              };
            }}
            columns={[
              {
                dataIndex: 'username',
                title: 'Username',
                align: 'center',
                key: 'username',
                sorter: true
              },
              {
                dataIndex: 'email',
                align: 'center',
                title: 'Email',
                key: 'email',
                sorter: true
              },
              {
                dataIndex: 'userType',
                align: 'center',
                title: 'Role',
                key: 'role',
                sorter: false
              },
              {
                dataIndex: 'userImage',
                title: 'User Image',
                align: 'center',
                key: 'userImage',
                render: value =>
                  value ? (
                    <div className="tableImage">
                      <Image className="tableImage" src={value} alt="" />
                    </div>
                  ) : (
                    'No Image'
                  )
              },
              {
                dataIndex: 'credit',
                align: 'center',
                title: 'Credit',
                key: 'credit',
                render: (value: number) => value?.toFixed(0)
              },
              {
                dataIndex: 'createdAt',
                title: 'Created At',
                key: 'createdAt',
                sorter: true,
                render: value => dayjs(value).format('DD/MM/YYYY HH:mm')
              },
              {
                dataIndex: 'subscription_start_date',
                title: 'Start Date',
                key: 'subscription_start_date',
                sorter: true,
                render: v => v ? dayjs(new Date(v * 1)).format('DD/MM/YYYY HH:mm') : '-'
              },
              {
                dataIndex: 'subscription_end_date',
                title: 'End Date',
                key: 'subscription_end_date',
                sorter: true,
                render: v => v ? dayjs(new Date(v * 1)).format('DD/MM/YYYY HH:mm') : '-'
              },
              ...[
                isAdmin
                  ? {
                    dataIndex: 'session',
                    title: 'Session Log',
                    key: 'session',
                    render: (value: any, record: any) => (
                      <Button
                        onClick={() => {
                          fetch(value).then(function(t) {
                            return t.text().then(b => {
                              setSessionViewData(b);
                            });
                          });
                        }}
                      >
                        View
                      </Button>
                    )
                  }
                  : {}
              ],
              ...[
                isAdmin
                  ? {
                    dataIndex: 'isDeleted',
                    title: 'Disable',
                    key: 'isDeleted',
                    sorter: true,
                    render: (value: boolean, record: IUser) => (
                      <Switch
                        checked={value}
                        onChange={(v, e) => {
                          e.stopPropagation();
                          disableUser({
                            id: record.id,
                            isDeleted: !record.isDeleted
                          });
                        }}
                        loading={record.id === ui.disableUserLoading}
                      />
                    )
                  }
                  : {}
              ],
              {
                key: 'Actions',
                title: 'Actions',
                render: (value, record: IUser) => (
                  <Button
                    type="default"
                    onClick={e => {
                      e.stopPropagation();
                      editForm.setFieldsValue({
                        credit: record.credit?.toFixed(0) || 0,
                        username: record.username,
                        userType: record.userType,
                        region: record?.region?.length
                          ? record.region
                          : regions.map(region => region.id),
                        countryOrigin: record.country_of_origin_id
                      });
                      setEditUserData(record);
                      setUserType(record.userType);
                    }}
                  >
                    <EditFilled />
                    Edit
                  </Button>
                )
              },
              {
                key: 'Send Email',
                title: 'Send Email',
                render: (value, record: IUser) => (
                  <Popconfirm
                    onConfirm={() => {
                      sendEmailWithCreds(record.id);
                    }}
                    placement="topRight"
                    onCancel={e => e?.stopPropagation()}
                    title={() => (
                      <p>
                        Send an email to <b>{record.email}</b> with{' '}
                        <b>new credentials</b>？
                      </p>
                    )}
                    icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
                  >
                    <Button disabled={sendEmailWithCredsLoading} type="default">
                      <MailOutlined />
                      Send Mail
                    </Button>
                  </Popconfirm>
                )
              }
            ]}
          ></Table>
          <Modal
            visible={editUserData ? true : false}
            title={`Edit ${editUserData?.username}`}
            onCancel={() => {
              setEditUserData(null);
              editForm.resetFields();
            }}
            onOk={editForm.submit}
            okButtonProps={{ loading: editUserData?.id === ui.editUserLoading }}
            destroyOnClose={true}
          >
            <Form
              {...layout}
              form={editForm}
              onFinish={async values => {
                editUserData?.id &&
                  (await editUser({
                    id: editUserData.id,
                    credit: values.credit,
                    username: values.username,
                    userType: values.userType,
                    region: values.region,
                    country_of_origin_id: values.countryOrigin,
                    ...(subscriptionEndDate != null && { subscription_end_date: subscriptionEndDate * 1000 }),
                    ...(subscriptionStartDate != null && { subscription_start_date: subscriptionStartDate * 1000 })
                  }));
                setEditUserData(null);
                getUsers({ ...data.getUsersParams, search: deSearch });
              }}
            >
              <Form.Item label="username" key="username" name="username">
                <Input />
              </Form.Item>
              <Form.Item label="credit" key="credit" name="credit">
                <Input type="number" min="0" />
              </Form.Item>
              <Form.Item label="User type" name="userType">
                <Select
                  placeholder="Select User Type"
                  onChange={e => {
                    setUserType(e);
                  }}
                  options={getCreateUserTypes(user?.userType).map(val => ({
                    key: val,
                    value: val,
                    label: val
                  }))}
                />
              </Form.Item>
              <Form.Item
                label="Region"
                name="region"
                rules={[
                  {
                    required: true,
                    message: 'Please Enter Region'
                  }
                ]}
              >
                <Select
                  tagRender={({ value }) => {
                    return (
                      <Tag>{regions.find(rg => rg.id === value)?.name}</Tag>
                    );
                  }}
                  loading={getRegionsLoading}
                  placeholder="Select Region"
                  mode="multiple"
                  showSearch={false}
                  dropdownRender={menu => <div>{menu}</div>}
                >
                  {regions?.map(({ name, id }) => (
                    <Select.Option key={id} value={id} className="regionOption">
                      <span>{name}</span>
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item
                label="Country Origin"
                name="countryOrigin"
                rules={[
                  {
                    required: true,
                    message: 'Please Enter Country Origin'
                  }
                ]}
              >
                <Select
                  tagRender={({ value }) => {
                    return (
                      <Tag>
                        {userRegions.find(rg => rg.id === value)?.country_name}
                      </Tag>
                    );
                  }}
                  loading={isUserRegionsLoading}
                  placeholder="Select Country Origin"
                >
                  {userRegions?.map(({ country_name, id }) => (
                    <Select.Option key={id} value={id} className="regionOption">
                      <span>{country_name}</span>
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              {editUserData?.userType === 'subscription' &&
                userType === 'subscription' ? (
                <Form.Item
                  label="Subscription Start"
                  name="subscriptionStartDate"
                  rules={[
                    {
                      required: true,
                      message: 'Please Enter Subscription Start Date'
                    }
                  ]}
                >
                  <DatePicker
                    onChange={onChangeStartDate}
                    format={dateFormatList}
                    defaultValue={dayjs(
                      new Date(editUserData?.subscription_start_date * 1)
                        .toLocaleDateString('en-US')
                    )}
                    disabledDate={disabledDate}
                    placeholder="Subscription Start date"
                  />
                </Form.Item>
              ) : null}
              {editUserData?.userType === 'subscription' &&
                userType === 'subscription' ? (
                <Form.Item
                  label="Subscription End"
                  name="subscriptionEndDate"
                  rules={[
                    {
                      required: true,
                      message: 'Please Enter Subscription End Date'
                    }
                  ]}
                >
                  <DatePicker
                    onChange={onChange}
                    format={dateFormatList}
                    defaultValue={dayjs(
                      new Date(editUserData?.subscription_end_date * 1)
                        .toLocaleDateString('en-US')
                    )}
                    placeholder="Subscription End date"
                    disabledDate={disabledDate}
                  />
                </Form.Item>
              ) : null}
            </Form>
          </Modal>
          <Modal
            visible={sessionViewData ? true : false}
            title={`Session Log`}
            className="sessionModel"
            onCancel={() => {
              setSessionViewData(null);
            }}
            footer={null}
          >
            <div>
              <div className="sessionModelContent">{sessionViewData}</div>
            </div>
          </Modal>
        </Card>
      </div>
    </div>
  );
});

export default Users;
